import { ArrowUpRight } from "lucide-react";
import { Image } from "@/components/ui/image";

/**
 * Small, responsive photo essay for published venue profiles with five or more
 * real photographs. Reuses the source gallery; never substitutes generic stock.
 * Numbers refer to the actual image's position in the business gallery.
 */
export default function BusinessPhotoStory({ photos = [], title, es = false, onOpen }) {
  if (photos.length < 5) return null;
  const picks = [2, 3, 4].filter((index) => !!photos[index]);
  if (picks.length < 3) return null;
  const open = (index) => onOpen?.(index);
  const alt = (index) => es
    ? `Otra imagen de ${title} — fotografía ${index + 1}`
    : `A closer look at ${title} — image ${index + 1}`;

  const Photo = ({ index, shape = "" }) => (
    <figure className={`group min-w-0 ${shape}`}>
      <button
        type="button"
        onClick={() => open(index)}
        aria-label={es ? `Abrir fotografía ${index + 1} de ${title}` : `Open photo ${index + 1} of ${title}`}
        className="relative block w-full overflow-hidden bg-[#242321] text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#d0352e] focus-visible:ring-offset-2"
      >
        <Image src={photos[index]} alt={alt(index)} fittingType="fill"
          quality={78} loading="lazy" decoding="async"
          className="h-full w-full object-cover transition-transform duration-700 motion-safe:group-hover:scale-[1.035]" />
        <span aria-hidden="true" className="absolute bottom-3 right-3 grid h-9 w-9 place-items-center bg-white/95 text-[#151515] opacity-90 transition group-hover:opacity-100">
          <ArrowUpRight size={18} strokeWidth={1.7}/>
        </span>
      </button>
      <figcaption className="mt-2 flex items-center gap-2 text-[9px] font-semibold uppercase tracking-[.11em] text-[#6e6a64] dark:text-white/65">
        <span className="font-bold tabular-nums text-[#ac4239] dark:text-[#efa69e]">{String(index + 1).padStart(2,"0")}</span>
        <span>{es ? "EN IMÁGENES" : "IN PICTURES"}</span>
      </figcaption>
    </figure>
  );

  return (
    <section className="mt-8 border-t border-[#d8d4cc] pt-5 dark:border-white/20 sm:mt-11 sm:pt-7" aria-label={es ? `Fotografías de ${title}` : `Photographs of ${title}`}>
      <div className="mb-5 flex items-baseline justify-between gap-3">
        <div>
          <p className="text-[9px] font-extrabold uppercase tracking-[.14em] text-[#a83d35] dark:text-[#f5a69d]">
            {es ? "DIARIO VISUAL / PUERTO RICO" : "THE PHOTO EDIT / PUERTO RICO"}
          </p>
          <h2 className="mt-1 text-[clamp(24px,4.6vw,34px)] font-extrabold leading-[1.05] tracking-[-.045em]">
            {es ? "Un poco más de cerca." : "A closer look."}
          </h2>
        </div>
        <span className="text-[10px] font-semibold tabular-nums text-[#827e76] dark:text-white/50">{photos.length} {es ? "FOTOS" : "PHOTOS"}</span>
      </div>
      <div className="grid grid-cols-[1.18fr_.82fr] items-start gap-3 sm:gap-5">
        <div className="min-w-0">
          <div className="[&_button]:aspect-[4/5] sm:[&_button]:aspect-[5/6]"><Photo index={picks[0]}/></div>
        </div>
        <div className="min-w-0 pt-[17%]">
          <div className="[&_button]:aspect-[3/4]"><Photo index={picks[1]}/></div>
        </div>
      </div>
      <div className="mt-5 ml-auto w-[88%] sm:mt-7 sm:w-[78%]">
        <div className="[&_button]:aspect-[16/9]"><Photo index={picks[2]}/></div>
      </div>
    </section>
  );
}
